import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { BehaviorSubject, filter, firstValueFrom, timeout } from 'rxjs';
import { ChatService } from './chat.service';
import { ChatUiService } from './chat-ui.service';
import { HubRequestError } from './core/hub-types';

/**
 * Chat notifications on THIS device (tasks/messaging-hub-architecture.md §9, M4).
 *
 *   browser   the Angular service worker subscribes (SwPush) with the hub's web push key; ngsw shows the notification
 *             and, on a click, focuses the app (notificationClicks here) or opens it at ?openChat=<id>
 *   iOS app   @capacitor/push-notifications: APNs token -> the hub (Notification Hubs sends it)
 *   Android   the same with FCM (needs google-services.json in the Android project; without it, "unavailable")
 *
 * Opt-in per device: the switch in the chat window. Once on, every start re-registers (the subscription or token can
 * change, and the hub keeps lastSeenAt). Signing out turns it off for this device, before storage is cleared.
 *
 *   state$   unsupported (no service worker / not a secure context), unavailable (no key or native setup), denied
 *            (the user blocked notifications), off, on
 */

export type ChatPushState = 'unknown' | 'unsupported' | 'unavailable' | 'denied' | 'off' | 'on';

const INSTALLATION_KEY = 'chat.push.installationId';
const ENABLED_KEY = 'chat.push.enabled';

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string | null): void => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // storage blocked: push works for this session only
  }
};

@Injectable({ providedIn: 'root' })
export class ChatPushService {
  private readonly state = new BehaviorSubject<ChatPushState>('unknown');
  readonly state$ = this.state.asObservable();
  private started = false;
  private readonly isBrowser: boolean;
  private readonly native: boolean;

  constructor(
    private readonly chat: ChatService,
    private readonly ui: ChatUiService,
    private readonly swPush: SwPush,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.native = this.isBrowser && Capacitor.isNativePlatform();
  }

  /** Called once the app shell is up (the chat window does it). Idempotent. */
  init(): void {
    if (!this.isBrowser || this.started) return;
    this.started = true;
    this.openFromUrl();
    if (this.native) this.initNative();
    else this.initWeb();
  }

  /** This device's id for the hub: made once, kept in storage (a sign-out clears it; the hub then matches the device). */
  private get installationId(): string {
    let id = read(INSTALLATION_KEY);
    if (!id) {
      id = `${this.native ? Capacitor.getPlatform() : 'web'}-${crypto.randomUUID()}`;
      write(INSTALLATION_KEY, id);
    }
    return id;
  }

  // ── opening from a notification ─────────────────────────────────────────────────────

  private openConversation(conversationId: unknown): void {
    if (typeof conversationId !== 'string' || !/^[0-9a-f-]{36}$/i.test(conversationId)) return;
    this.zone.run(() => this.ui.openConversation(conversationId.toLowerCase()));
  }

  /** Opened by a notification while no tab was open: ?openChat=<id>. The parameter is removed once used. */
  private openFromUrl(): void {
    const url = new URL(window.location.href);
    const id = url.searchParams.get('openChat');
    if (!id) return;
    url.searchParams.delete('openChat');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    void this.chat.ensureStarted().then(() => this.openConversation(id));
  }

  // ── web ─────────────────────────────────────────────────────────────────────────────

  private initWeb(): void {
    if (!this.swPush.isEnabled || typeof Notification === 'undefined') {
      this.state.next('unsupported');
      return;
    }
    this.swPush.notificationClicks.subscribe(({ notification }) => this.openConversation((notification?.data as { conversationId?: unknown } | undefined)?.conversationId));
    if (Notification.permission === 'denied') {
      this.state.next('denied');
      return;
    }
    if (read(ENABLED_KEY) === '1' && Notification.permission === 'granted') void this.enable().catch(() => this.state.next('off'));
    else this.state.next('off');
  }

  private async enableWeb(): Promise<void> {
    const { webPublicKey } = await this.chat.store.pushConfig();
    if (!webPublicKey) {
      this.state.next('unavailable');
      return;
    }
    let sub: PushSubscription;
    try {
      sub = await this.swPush.requestSubscription({ serverPublicKey: webPublicKey });
    } catch (e) {
      // An old subscription made with another key (the key changed): drop it and subscribe again.
      if ((e as Error)?.name === 'InvalidStateError') {
        await this.swPush.unsubscribe().catch(() => undefined);
        sub = await this.swPush.requestSubscription({ serverPublicKey: webPublicKey });
      } else {
        this.state.next(typeof Notification !== 'undefined' && Notification.permission === 'denied' ? 'denied' : 'off');
        return;
      }
    }
    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.['p256dh'] || !json.keys?.['auth']) throw new HubRequestError('BAD_REQUEST');
    await this.chat.store.pushRegister({
      installationId: this.installationId,
      platform: 'web',
      channel: { endpoint: json.endpoint, p256dh: json.keys['p256dh'], auth: json.keys['auth'] },
    });
    write(ENABLED_KEY, '1');
    this.state.next('on');
  }

  // ── native (Capacitor) ──────────────────────────────────────────────────────────────

  private initNative(): void {
    void PushNotifications.addListener('registration', (t) => void this.registerNativeToken(t.value));
    void PushNotifications.addListener('registrationError', () => this.zone.run(() => this.state.next('unavailable')));
    void PushNotifications.addListener('pushNotificationActionPerformed', (a) => this.openConversation((a.notification?.data as { conversationId?: unknown } | undefined)?.conversationId));
    void PushNotifications.checkPermissions().then((p) => {
      if (p.receive === 'denied') this.zone.run(() => this.state.next('denied'));
      else if (read(ENABLED_KEY) === '1' && p.receive === 'granted') void this.enable().catch(() => this.zone.run(() => this.state.next('off')));
      else this.zone.run(() => this.state.next('off'));
    });
  }

  private async enableNative(): Promise<void> {
    let p = await PushNotifications.checkPermissions();
    if (p.receive === 'prompt' || p.receive === 'prompt-with-rationale') p = await PushNotifications.requestPermissions();
    if (p.receive !== 'granted') {
      this.state.next('denied');
      return;
    }
    write(ENABLED_KEY, '1');
    try {
      await PushNotifications.register(); // the token arrives in the 'registration' listener
    } catch {
      this.state.next('unavailable'); // e.g. Android without Firebase set up
    }
  }

  private async registerNativeToken(token: string): Promise<void> {
    try {
      await this.whenChatReady();
      await this.chat.store.pushRegister({ installationId: this.installationId, platform: Capacitor.getPlatform() === 'ios' ? 'apns' : 'fcm', channel: { token } });
      this.zone.run(() => this.state.next('on'));
    } catch {
      this.zone.run(() => this.state.next('off'));
    }
  }

  // ── the switch ──────────────────────────────────────────────────────────────────────

  private async whenChatReady(): Promise<void> {
    await this.chat.ensureStarted();
    await firstValueFrom(this.chat.store.state$.pipe(filter((s) => !!s.me), timeout(20_000)));
  }

  /** Turns chat notifications on for this device (asks for permission the first time: call it from a click). */
  async enable(): Promise<void> {
    await this.whenChatReady();
    if (this.native) await this.enableNative();
    else await this.enableWeb();
  }

  /** Turns them off for this device. */
  async disable(): Promise<void> {
    write(ENABLED_KEY, null);
    this.state.next('off');
    await this.chat.store.pushUnregister(this.installationId).catch(() => undefined);
    if (this.native) await PushNotifications.unregister().catch(() => undefined);
    else if (this.swPush.isEnabled) await this.swPush.unsubscribe().catch(() => undefined);
  }

  /**
   * Signing out: this device stops receiving the user's chat notifications. Best effort and quick (the page is about to
   * be replaced): the hub is told over the open socket, and the browser subscription is dropped, which makes any later
   * push to it fail as gone.
   */
  signOut(): void {
    if (!this.isBrowser || read(ENABLED_KEY) !== '1') return;
    void this.disable();
  }
}
